import React, { useState } from "react";
import { ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { SEO } from "@/components/common/SEO";
import { track } from "@/utils/analytics";

const APP_URL = "https://app.vawena.ai";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const QUIZ_WEBHOOK = (import.meta as any).env?.VITE_QUIZ_WEBHOOK_URL as string | undefined;

// ── Quiz data ─────────────────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: "properties",
    question: "How many rental properties do you currently own?",
    options: [
      { label: "None — I'm buying my first", points: 0 },
      { label: "1 property", points: 1 },
      { label: "2–5 properties", points: 2 },
      { label: "6 or more", points: 3 },
    ],
  },
  {
    id: "income",
    question: "What is your approximate monthly take-home income?",
    options: [
      { label: "Less than $4,000", points: 0 },
      { label: "$4,000 – $7,000", points: 1 },
      { label: "$7,000 – $12,000", points: 2 },
      { label: "Over $12,000", points: 3 },
    ],
  },
  {
    id: "downpayment",
    question: "How much do you have saved for a down payment?",
    options: [
      { label: "Less than $15,000", points: 0 },
      { label: "$15,000 – $40,000", points: 1 },
      { label: "$40,000 – $80,000", points: 2 },
      { label: "Over $80,000", points: 3 },
    ],
  },
  {
    id: "dti",
    question: "What's your current debt-to-income (DTI) ratio?",
    options: [
      { label: "I'm not sure", points: 0 },
      { label: "Above 45%", points: 0 },
      { label: "36% – 45%", points: 1 },
      { label: "Below 36%", points: 3 },
    ],
  },
  {
    id: "debt",
    question: "How much high-interest debt do you carry? (credit cards, personal loans)",
    options: [
      { label: "More than $20,000", points: 0 },
      { label: "$10,000 – $20,000", points: 1 },
      { label: "Under $10,000", points: 2 },
      { label: "None", points: 3 },
    ],
  },
  {
    id: "emergency",
    question: "Do you have at least 6 months of expenses saved as an emergency fund?",
    options: [
      { label: "No", points: 0 },
      { label: "Partially (1–3 months)", points: 1 },
      { label: "Yes", points: 3 },
    ],
  },
  {
    id: "credit",
    question: "Have you reviewed your credit score in the last 3 months?",
    options: [
      { label: "No", points: 0 },
      { label: "Yes, it's below 640", points: 0 },
      { label: "Yes, it's 640–720", points: 2 },
      { label: "Yes, it's above 720", points: 3 },
    ],
  },
] as const;

const MAX_SCORE = 21;

// ── Result tiers ──────────────────────────────────────────────────────────────

function getResult(score: number) {
  const pct = Math.round((score / MAX_SCORE) * 100);
  if (score <= 7) {
    return {
      tier: "Building the Foundation",
      color: "text-blue-400",
      barColor: "bg-blue-500",
      pct,
      summary:
        "You're not quite ready yet — but that's exactly why this quiz exists. Your numbers show a few key gaps that would likely prevent lender approval right now.",
      next: "WealthScope will show you your exact buying power, DTI, and the specific steps to close the gap — so when you're ready, you already know it.",
      cta: "Start building my plan",
    };
  }
  if (score <= 13) {
    return {
      tier: "On the Path",
      color: "text-cyan-400",
      barColor: "bg-cyan-500",
      pct,
      summary:
        "You have a solid foundation but a few things to address before you'd comfortably qualify. Most investors at this stage are 2–6 months away from being deal-ready.",
      next: "WealthScope tracks your progress month by month — so you'll know the exact moment your numbers support your next acquisition.",
      cta: "Track my path to readiness",
    };
  }
  if (score <= 18) {
    return {
      tier: "Almost There",
      color: "text-blue-300",
      barColor: "bg-blue-400",
      pct,
      summary:
        "Strong financial position. You're likely close to qualifying — but 'close' isn't the same as 'ready.' One or two metrics may still hold you back with a lender.",
      next: "WealthScope calculates your precise deal readiness score and shows exactly which number to optimize first.",
      cta: "Verify my readiness score",
    };
  }
  return {
    tier: "Ready to Invest",
    color: "text-emerald-400",
    barColor: "bg-emerald-500",
    pct,
    summary:
      "Your financial profile is strong. Based on your answers, your numbers should support your next real estate acquisition — you just need to confirm the exact figures.",
    next: "WealthScope will calculate your precise buying power, confirm your DTI, and generate a lender-ready summary so you can move fast when the right deal appears.",
    cta: "See my exact numbers",
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

type Answers = Record<string, number>;
type Stage = "quiz" | "email" | "result";

const InvestmentReadinessQuiz: React.FC = () => {
  const [stage, setStage] = useState<Stage>("quiz");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [selected, setSelected] = useState<number | null>(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState(0);

  const totalQ = QUESTIONS.length;
  const progress = ((currentQ) / totalQ) * 100;

  function handleSelect(points: number) {
    setSelected(points);
  }

  function handleNext() {
    if (selected === null) return;
    const q = QUESTIONS[currentQ];
    const updated = { ...answers, [q.id]: selected };
    setAnswers(updated);
    setSelected(null);

    if (currentQ < totalQ - 1) {
      setCurrentQ((c) => c + 1);
    } else {
      // All questions answered — go to email step
      const total = Object.values(updated).reduce((a, b) => a + b, 0);
      setScore(total);
      setStage("email");
      track.quizCompleted(total);
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);

    if (QUIZ_WEBHOOK && email) {
      try {
        await fetch(QUIZ_WEBHOOK, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, score, answers, source: "investment-readiness-quiz" }),
        });
      } catch {
        // Fail silently — still show results
      }
    }

    setSubmitting(false);
    setStage("result");
    if (email) track.quizEmailCaptured();
  }

  const result = getResult(score);

  // ── Result screen ──────────────────────────────────────────────────────────
  if (stage === "result") {
    return (
      <>
        <SEO
          title="Your Investment Readiness Score — VaWeNa"
          description="See your personalized investment readiness score and find out exactly when you're ready to buy your next property."
          canonical="/quiz"
        />
        <div className="min-h-screen dark:bg-vw-bg bg-[#f0f4ff] flex items-center justify-center px-4 py-20">
          <div className="w-full max-w-[600px]">
            {/* Score card */}
            <div className="dark:bg-white/[0.03] bg-white border dark:border-white/10 border-gray-200 rounded-3xl p-8 sm:p-10 text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-blue-500/10 bg-blue-100 dark:border dark:border-blue-500/20 border border-blue-200 dark:text-blue-400 text-blue-700 text-xs font-bold tracking-widest uppercase mb-6">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Your Readiness Score
              </div>

              {/* Score ring */}
              <div className="relative w-32 h-32 mx-auto mb-6">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="8" className="dark:text-white/5 text-gray-200" />
                  <circle
                    cx="60" cy="60" r="50" fill="none" strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    strokeDashoffset={`${2 * Math.PI * 50 * (1 - result.pct / 100)}`}
                    className={result.barColor.replace("bg-", "stroke-")}
                    style={{ transition: "stroke-dashoffset 1s ease" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-3xl font-extrabold ${result.color}`}>{result.pct}%</span>
                  <span className="text-xs dark:text-slate-500 text-gray-400">ready</span>
                </div>
              </div>

              <h2 className={`text-2xl font-extrabold mb-3 ${result.color}`}>{result.tier}</h2>
              <p className="dark:text-slate-400 text-gray-600 text-sm leading-relaxed mb-6 max-w-md mx-auto">
                {result.summary}
              </p>

              {/* Score bar */}
              <div className="w-full dark:bg-white/5 bg-gray-100 rounded-full h-2 mb-1 overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-1000 ${result.barColor}`}
                  style={{ width: `${result.pct}%` }}
                />
              </div>
              <div className="flex justify-between text-xs dark:text-slate-600 text-gray-400 mt-1 mb-6">
                <span>Building</span>
                <span>Score: {score} / {MAX_SCORE}</span>
                <span>Ready</span>
              </div>

              {/* What's next */}
              <div className="dark:bg-blue-500/5 bg-blue-50 border dark:border-blue-500/15 border-blue-200 rounded-2xl p-5 text-left mb-6">
                <p className="text-xs font-bold uppercase tracking-widest dark:text-blue-400 text-blue-600 mb-2">What to do next</p>
                <p className="text-sm dark:text-slate-300 text-gray-700 leading-relaxed">{result.next}</p>
              </div>

              <a
                href={`${APP_URL}/signup`}
                target="_self"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 w-full justify-center h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-colors"
              >
                {result.cta} <ArrowRight className="w-4 h-4" />
              </a>
              <p className="mt-3 text-xs dark:text-slate-500 text-gray-400">14-day free trial · Credit card required · We'll remind you before you're charged</p>
            </div>

            {/* Retake */}
            <p className="text-center text-xs dark:text-slate-600 text-gray-400">
              Want to retake the quiz?{" "}
              <button
                type="button"
                onClick={() => { setStage("quiz"); setCurrentQ(0); setAnswers({}); setSelected(null); setEmail(""); }}
                className="dark:text-blue-400 text-blue-600 hover:underline"
              >
                Start over
              </button>
            </p>
          </div>
        </div>
      </>
    );
  }

  // ── Email capture screen ───────────────────────────────────────────────────
  if (stage === "email") {
    return (
      <>
        <SEO
          title="Almost There — VaWeNa Investment Readiness Quiz"
          description="Enter your email to see your personalized investment readiness score."
          canonical="/quiz"
        />
        <div className="min-h-screen dark:bg-vw-bg bg-[#f0f4ff] flex items-center justify-center px-4 py-20">
          <div className="w-full max-w-[480px]">
            <div className="dark:bg-white/[0.03] bg-white border dark:border-white/10 border-gray-200 rounded-3xl p-8 sm:p-10 text-center">
              <div className="w-14 h-14 rounded-2xl dark:bg-blue-500/10 bg-blue-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-blue-400" />
              </div>
              <h2 className="text-2xl font-extrabold dark:text-white text-gray-900 mb-2">
                Your score is ready
              </h2>
              <p className="dark:text-slate-400 text-gray-500 text-sm mb-8 leading-relaxed">
                Enter your email to see your personalized investment readiness score and what it means for your next deal.
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 rounded-full px-5 dark:bg-white/5 bg-gray-50 dark:border dark:border-white/10 border border-gray-200 dark:text-white text-gray-900 dark:placeholder-slate-500 placeholder-gray-400 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1 focus:ring-2 focus:ring-blue-500/50"
                />
                <button
                  type="submit"
                  disabled={submitting || !email}
                  className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? "Loading..." : <>See My Score <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
              <p className="mt-4 text-xs dark:text-slate-600 text-gray-400">
                No spam. We'll send you your score and occasional investor tips. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Quiz screen ────────────────────────────────────────────────────────────
  const q = QUESTIONS[currentQ];

  return (
    <>
      <SEO
        title="Investment Readiness Quiz — Are You Ready to Buy Your Next Property?"
        description="Take the 2-minute quiz to find out if you're financially ready to invest in real estate. Get your personalized readiness score instantly."
        canonical="/quiz"
      />
      <div className="min-h-screen dark:bg-vw-bg bg-[#f0f4ff] flex flex-col items-center justify-center px-4 py-20">
        <div className="w-full max-w-[560px]">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-blue-500/10 bg-blue-100 dark:border dark:border-blue-500/20 border border-blue-200 dark:text-blue-400 text-blue-700 text-xs font-bold tracking-widest uppercase mb-4">
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
              Investment Readiness Quiz
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold dark:text-white text-gray-900 mb-2">
              Are you ready to buy your next property?
            </h1>
            <p className="text-sm dark:text-slate-400 text-gray-500">7 questions · 2 minutes · Free score</p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-xs dark:text-slate-500 text-gray-400 mb-2">
              <span>Question {currentQ + 1} of {totalQ}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <div className="w-full dark:bg-white/5 bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-1.5 rounded-full bg-blue-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question card */}
          <div className="dark:bg-white/[0.03] bg-white border dark:border-white/10 border-gray-200 rounded-3xl p-6 sm:p-8">
            <h2 className="text-lg font-bold dark:text-white text-gray-900 mb-6 leading-snug">
              {q.question}
            </h2>

            <div className="space-y-3 mb-8">
              {q.options.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => handleSelect(opt.points)}
                  className={`w-full text-left px-5 py-3.5 rounded-2xl border text-sm font-medium transition-all duration-200 flex items-center justify-between gap-3 ${
                    selected === opt.points
                      ? "dark:bg-blue-500/15 bg-blue-50 dark:border-blue-500/40 border-blue-400 dark:text-white text-blue-700"
                      : "dark:bg-white/[0.02] bg-gray-50 dark:border-white/10 border-gray-200 dark:text-slate-300 text-gray-700 dark:hover:border-blue-500/25 hover:border-blue-200 dark:hover:bg-white/[0.04] hover:bg-blue-50/50"
                  }`}
                >
                  <span>{opt.label}</span>
                  {selected === opt.points && (
                    <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />
                  )}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={selected === null}
              className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
            >
              {currentQ < totalQ - 1 ? (
                <>Next <ChevronRight className="w-4 h-4" /></>
              ) : (
                <>See My Score <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>

          <p className="text-center text-xs dark:text-slate-600 text-gray-400 mt-6">
            Free · No sign-up required to take the quiz
          </p>
        </div>
      </div>
    </>
  );
};

export default InvestmentReadinessQuiz;
